import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface MoneyConfettiProps {
  active: boolean;
  duration?: number;
}

const MONEY_EMOJIS = ['💵', '💰', '🤑', '💎', '🪙', '💳', '🏧', '💰'];

interface ConfettiPiece {
  id: number;
  emoji: string;
  x: number;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
}

const MoneyConfetti: React.FC<MoneyConfettiProps> = ({ active, duration = 5000 }) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  useEffect(() => {
    if (!active) {
      setPieces([]);
      return;
    }

    // Generate confetti pieces
    const newPieces: ConfettiPiece[] = [];
    for (let i = 0; i < 60; i++) {
      newPieces.push({
        id: i,
        emoji: MONEY_EMOJIS[Math.floor(Math.random() * MONEY_EMOJIS.length)],
        x: Math.random() * 100, // percentage across screen
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 3,
        size: 20 + Math.random() * 24,
        rotation: Math.random() * 360,
      });
    }
    setPieces(newPieces);

    // Clear after duration
    const timer = setTimeout(() => {
      setPieces([]);
    }, duration);

    return () => clearTimeout(timer);
  }, [active, duration]);

  return (
    <AnimatePresence>
      {active && pieces.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {pieces.map((piece) => (
            <motion.div
              key={piece.id}
              className="absolute"
              style={{
                left: `${piece.x}%`,
                fontSize: `${piece.size}px`,
              }}
              initial={{ 
                y: -50, 
                opacity: 1,
                rotate: 0,
                scale: 0,
              }}
              animate={{ 
                y: '110vh', 
                opacity: [1, 1, 1, 0],
                rotate: piece.rotation + 720,
                scale: [0, 1.2, 1, 0.8],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: piece.duration,
                delay: piece.delay,
                ease: 'easeIn',
              }}
            >
              {piece.emoji}
            </motion.div>
          ))}
          
          {/* Center celebration text */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1, 0.8] }}
            transition={{ duration: 3, times: [0, 0.2, 0.7, 1] }}
          >
            <div className="text-center">
              <motion.div
                className="text-8xl mb-4"
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.3, 1],
                }}
                transition={{ duration: 0.8, repeat: Infinity }}
              >
                💰
              </motion.div>
              <h2 className="text-5xl font-black text-yellow-400 drop-shadow-[0_0_30px_rgba(250,204,21,0.5)]">
                FAME &amp; FORTUNE!
              </h2>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MoneyConfetti;
